import * as React from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Select } from 'radix-ui';
import classnames from 'classnames';

export interface SelectItem {
  key: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface GenericSelectProps<T extends SelectItem> {
  items: T[];
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  error?: string;
  onValueChange?: (value: string) => void;
  renderItem?: (item: T) => React.ReactNode;
}

const GenericSelect = React.forwardRef<
  HTMLButtonElement,
  GenericSelectProps<SelectItem>
>(
  (
    {
      items,
      value,
      placeholder = 'Select...',
      disabled,
      label = 'Items',
      className = '',
      onValueChange,
      renderItem,
      error,
    },
    ref
  ) => {

    return (
      <Select.Root
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <Select.Trigger
          className={classnames(
            `flex w-full items-center justify-between rounded-md border border-input bg-inherit px-4 py-3 ring-offset-background disabled:cursor-not-allowed disabled:opacity-50`,
            `[&>span[data-placeholder]]:text-gray-400`,
            className,
            error ? 'border-red-300' : 'border-gray-300'
          )}
          ref={ref}
        >
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>
            <Select.Value placeholder={placeholder} />
          </span>
          <Select.Icon className="opacity-50">
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </Select.Icon>
        </Select.Trigger>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

        <Select.Portal>
          <Select.Content className="relative max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
            <Select.ScrollUpButton className="flex items-center justify-center py-1">
              <ChevronUp className="w-4 h-4 text-gray-400" />
            </Select.ScrollUpButton>

            <Select.Viewport className="p-1">
              <Select.Group>
                <Select.Label className="py-1.5 pl-8 pr-2 text-sm font-semibold">
                  {label}
                </Select.Label>

                {items.map((item) => (
                  <SelectItem key={item.key} value={item.key}>
                    <Select.ItemText>
                      {renderItem?.(item) ?? item.label}
                    </Select.ItemText>
                  </SelectItem>
                ))}
              </Select.Group>
            </Select.Viewport>

            <Select.ScrollDownButton className="flex items-center justify-center py-1">
              <ChevronDown />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    );
  }
);

const SelectItem = React.forwardRef<HTMLDivElement, Select.SelectItemProps>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <Select.Item
        className="relative flex cursor-default select-none items-center rounded pl-8 px-4 py-3 text-sm outline-none focus:bg-accent"
        {...props}
        ref={forwardedRef}
      >
        {children}
        <Select.ItemIndicator className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check className="h-4 w-4" />
        </Select.ItemIndicator>
      </Select.Item>
    );
  }
);

export { GenericSelect };
